import React, { useState, useRef, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogTrigger, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ZoomIn, ZoomOut, ArrowLeft, ChevronLeft, ChevronRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Fullscreen media viewer with pinch-zoom, drag, double-tap, and Back button.
 * Supports single or multiple photos with prev/next.
 */
export default function FullscreenMedia({ path, paths, initialIndex = 0, alt = "media", testid = "fullscreen-media", children }) {
  const list = paths && paths.length ? paths : (path ? [path] : []);
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(initialIndex);
  const [zoom, setZoom] = useState(1);
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);
  const containerRef = useRef(null);
  const gesture = useRef({ dragging: false, startX: 0, startY: 0, startTx: 0, startTy: 0, lastDist: 0, lastTap: 0 });

  const resetView = useCallback(() => { setZoom(1); setTx(0); setTy(0); }, []);

  useEffect(() => { if (!open) resetView(); }, [open, resetView]);
  useEffect(() => { resetView(); }, [idx, resetView]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === "Escape") setOpen(false);
      else if (e.key === "ArrowRight" && list.length > 1) setIdx((i) => (i + 1) % list.length);
      else if (e.key === "ArrowLeft" && list.length > 1) setIdx((i) => (i - 1 + list.length) % list.length);
      else if (e.key === "+") setZoom((z) => Math.min(5, z + 0.25));
      else if (e.key === "-") setZoom((z) => Math.max(0.5, z - 0.25));
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, list.length]);

  const dist2 = (t1, t2) => Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);

  const onPointerStart = (e) => {
    if (e.touches && e.touches.length === 2) {
      gesture.current.lastDist = dist2(e.touches[0], e.touches[1]);
      return;
    }
    const t = e.touches ? e.touches[0] : e;
    gesture.current.dragging = true;
    gesture.current.startX = t.clientX;
    gesture.current.startY = t.clientY;
    gesture.current.startTx = tx;
    gesture.current.startTy = ty;
    // Double tap detection
    const now = Date.now();
    if (now - gesture.current.lastTap < 300 && e.touches && e.touches.length === 1) {
      if (zoom > 1) { resetView(); } else { setZoom(2.5); }
    }
    gesture.current.lastTap = now;
  };
  const onPointerMove = (e) => {
    if (e.touches && e.touches.length === 2) {
      const d = dist2(e.touches[0], e.touches[1]);
      if (gesture.current.lastDist > 0) {
        const factor = d / gesture.current.lastDist;
        setZoom((z) => Math.max(0.5, Math.min(5, z * factor)));
      }
      gesture.current.lastDist = d;
      e.preventDefault();
      return;
    }
    if (!gesture.current.dragging) return;
    if (zoom <= 1) return;
    const t = e.touches ? e.touches[0] : e;
    setTx(gesture.current.startTx + (t.clientX - gesture.current.startX));
    setTy(gesture.current.startTy + (t.clientY - gesture.current.startY));
  };
  const onPointerEnd = () => {
    gesture.current.dragging = false;
    gesture.current.lastDist = 0;
  };

  const onWheel = (e) => {
    if (!open) return;
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.15 : 0.15;
    setZoom((z) => Math.max(0.5, Math.min(5, z + delta)));
  };

  const src = list[idx]?.startsWith("http") ? list[idx] : `${BACKEND_URL}/api/files/${list[idx]}`;
  const zoomPct = Math.round(((zoom - 0.5) / 4.5) * 100);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent
        className="max-w-[100vw] w-screen h-[100dvh] p-0 bg-black border-0 overflow-hidden rounded-none"
        data-testid={testid}
      >
        <DialogTitle className="sr-only">{alt}</DialogTitle>

        {/* Back button */}
        <button
          data-testid={`${testid}-back`}
          onClick={() => setOpen(false)}
          className="absolute top-4 left-4 z-30 inline-flex items-center gap-2 h-10 px-4 rounded-full glass-dark hover:bg-white/25 text-white text-sm font-semibold border border-white/25"
          aria-label="Indietro"
        >
          <ArrowLeft size={16} /> Indietro
        </button>

        {/* Zoom indicator top-right */}
        <div className="absolute top-4 right-4 z-30 glass-dark rounded-full px-3 py-1.5 text-xs font-mono text-white tabular-nums" data-testid={`${testid}-zoom-indicator`}>
          {zoomPct}%
        </div>

        {/* Prev/Next for multi-image */}
        {list.length > 1 && (
          <>
            <button data-testid={`${testid}-prev`} onClick={() => setIdx((i) => (i - 1 + list.length) % list.length)} className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full glass-dark hover:bg-white/25 text-white flex items-center justify-center" aria-label="Foto precedente"><ChevronLeft size={20} /></button>
            <button data-testid={`${testid}-next`} onClick={() => setIdx((i) => (i + 1) % list.length)} className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full glass-dark hover:bg-white/25 text-white flex items-center justify-center" aria-label="Foto successiva"><ChevronRight size={20} /></button>
          </>
        )}

        <div
          ref={containerRef}
          className="relative w-full h-full flex items-center justify-center overflow-hidden touch-none select-none"
          onWheel={onWheel}
          onMouseDown={onPointerStart}
          onMouseMove={onPointerMove}
          onMouseUp={onPointerEnd}
          onMouseLeave={onPointerEnd}
          onTouchStart={onPointerStart}
          onTouchMove={onPointerMove}
          onTouchEnd={onPointerEnd}
        >
          <img
            src={src}
            alt={alt}
            className="select-none block"
            style={{
              maxWidth: "100vw",
              maxHeight: "100dvh",
              width: "auto",
              height: "auto",
              objectFit: "contain",
              transform: `translate3d(${tx}px, ${ty}px, 0) scale(${zoom})`,
              transformOrigin: "center center",
              transition: gesture.current.dragging ? "none" : "transform 0.15s ease-out",
              cursor: zoom > 1 ? "grab" : "default",
            }}
            draggable={false}
          />
        </div>

        {/* Controls bottom */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2 glass-dark rounded-full p-1.5">
          <Button data-testid={`${testid}-zoom-out`} size="icon" variant="ghost" onClick={() => setZoom((z) => Math.max(0.5, z - 0.25))} className="h-10 w-10 rounded-full text-white hover:bg-white/20 hover:text-white"><ZoomOut size={16} /></Button>
          <input
            type="range"
            min="50"
            max="500"
            value={Math.round(zoom * 100)}
            onChange={(e) => setZoom(parseInt(e.target.value) / 100)}
            className="w-32 md:w-48 accent-emerald-500"
            data-testid={`${testid}-zoom-slider`}
          />
          <Button data-testid={`${testid}-zoom-in`} size="icon" variant="ghost" onClick={() => setZoom((z) => Math.min(5, z + 0.25))} className="h-10 w-10 rounded-full text-white hover:bg-white/20 hover:text-white"><ZoomIn size={16} /></Button>
          <Button data-testid={`${testid}-reset`} size="sm" variant="ghost" onClick={resetView} className="h-10 rounded-full text-white hover:bg-white/20 hover:text-white text-xs">Reset</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
